import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";

import { HubBlobUrlPreview } from "@/components/HubBlobUrlPreview";
import {
  HubWorkspaceBody,
  HubWorkspaceHeader,
  HubWorkspacePage,
  HubWorkspacePanel,
} from "@/components/layout/HubWorkspace";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { FilePicker } from "@/components/ui/FilePicker";
import { useHubToast } from "@/components/ui/HubToast";
import { CardTitleWithHelp, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";

import { SuntronicInvoiceWorkMatchingColumn } from "./SuntronicInvoiceWorkMatching";
import {
  deleteSuntronicInvoiceDocument,
  fetchSuntronicInvoiceDocumentUrl,
  fetchSuntronicInvoiceDocuments,
  fetchSuntronicInvoiceOptions,
  uploadSuntronicInvoiceDocuments,
  type SuntronicInvoiceDocumentRow,
} from "./suntronicInvoicesApi";

const PAGE_HELP =
  "Match finished goods on the left and review manufacturer invoice PDFs on the right. You can add or remove PDFs until the invoice is reconciled.";

const SUNTRONIC_QUERY_ROOT = ["accounting", "suntronic-invoices"] as const;

const READ_ONLY_HINT = "Your role has read-only access to the Accounting hub module.";

function describeMutationError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export function SuntronicInvoiceWorkPage(): JSX.Element {
  const { invoiceId: invoiceIdParam } = useParams<{ invoiceId: string }>();
  const invoiceId = Number.parseInt(invoiceIdParam ?? "", 10);
  const invoiceIdValid = Number.isInteger(invoiceId) && invoiceId > 0;
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const canWrite = useHubModuleWrite("accounting");
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-options", true],
    queryFn: () => fetchSuntronicInvoiceOptions(true),
    enabled: invoiceIdValid,
  });

  const documentsQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-documents", invoiceId],
    queryFn: () => fetchSuntronicInvoiceDocuments(invoiceId),
    enabled: invoiceIdValid,
  });

  const invoice = useMemo(
    () => listQuery.data?.find((row) => row.value === invoiceId) ?? null,
    [invoiceId, listQuery.data],
  );
  const documents = documentsQuery.data ?? [];
  const selectedDocument =
    documents.find((document) => document.id === selectedDocumentId) ?? documents[0] ?? null;
  const pdfsLocked = invoice?.reconciled === true || !canWrite;

  useEffect(() => {
    if (selectedDocumentId != null && documents.some((document) => document.id === selectedDocumentId)) {
      return;
    }
    setSelectedDocumentId(documents[0]?.id ?? null);
  }, [documents, selectedDocumentId]);

  const previewQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-document-url", invoiceId, selectedDocument?.id],
    queryFn: () => fetchSuntronicInvoiceDocumentUrl(invoiceId, selectedDocument!.id),
    enabled: invoiceIdValid && selectedDocument != null,
    staleTime: 60_000,
  });

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => uploadSuntronicInvoiceDocuments(invoiceId, files),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-documents", invoiceId] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: describeMutationError(error, "Could not upload PDFs.") });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => deleteSuntronicInvoiceDocument(invoiceId, documentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-documents", invoiceId] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: describeMutationError(error, "Could not remove that PDF.") });
    },
  });

  const title = invoice?.number ?? (invoiceIdValid ? `Invoice ${invoiceId}` : "Manufacturer invoice");

  function applyDroppedFiles(files: File[]): void {
    if (pdfsLocked || files.length === 0 || uploadMutation.isPending) {
      return;
    }
    const invalidPdf = files.find(
      (file) => file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf"),
    );
    if (invalidPdf != null) {
      showToast({ tone: "error", message: "Invoice attachments must be PDF files." });
      return;
    }
    uploadMutation.mutate(files);
  }

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>{title}</PageTitleWithHelp>
      </HubWorkspaceHeader>

      {!invoiceIdValid ? (
        <p className="text-sm text-rose-700">That manufacturer invoice id is not valid.</p>
      ) : listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading manufacturer invoice…
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-rose-700">Could not load this manufacturer invoice.</p>
      ) : invoice == null ? (
        <p className="text-sm text-slate-600">No manufacturer invoice matches this id.</p>
      ) : (
        <HubWorkspaceBody className="grid gap-4 lg:grid-cols-[minmax(22rem,48%)_minmax(0,1fr)] lg:items-stretch">
          <HubWorkspacePanel scroll="auto" className="min-w-0">
            <SuntronicInvoiceWorkMatchingColumn
              invoiceId={invoiceId}
              invoiceTotal={invoice.total_cost_raw}
              invoiceNumber={invoice.number}
              manufacturer={invoice.manufacturer}
              invoiceDate={invoice.date}
              reconciled={invoice.reconciled}
              canWrite={canWrite}
              onError={(message) => showToast({ tone: "error", message })}
            />
          </HubWorkspacePanel>

          <HubWorkspacePanel scroll="hidden" className="min-w-0">
          <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <CardHeader className="shrink-0">
              <CardTitleWithHelp hoverHint="Invoice and packing-slip PDFs attached to this manufacturer invoice.">
                Invoice PDFs
              </CardTitleWithHelp>
            </CardHeader>
            <CardBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              {invoice.reconciled ? null : (
                <ReadOnlyHoverLayer active={!canWrite} hoverHint={READ_ONLY_HINT} layout="block">
                  <FilePicker
                    key={documentsQuery.dataUpdatedAt}
                    id="work-invoice-pdfs"
                    label="Add PDFs"
                    hoverHint="Drop one or more PDFs onto this row, or browse. You can attach the invoice and the packing slip together."
                    emptyLabel="Drop PDFs here or browse"
                    accept="application/pdf,.pdf"
                    multiple
                    disabled={!canWrite || uploadMutation.isPending}
                    onChange={(_file, event) => {
                      applyDroppedFiles(Array.from(event.target.files ?? []));
                    }}
                  />
                </ReadOnlyHoverLayer>
              )}
              {uploadMutation.isPending ? (
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Uploading PDFs…
                </p>
              ) : null}
              {documentsQuery.isLoading ? (
                <div className="flex shrink-0 items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading PDFs…
                </div>
              ) : documentsQuery.isError ? (
                <p className="shrink-0 text-sm text-rose-700">Could not load invoice PDFs.</p>
              ) : documents.length === 0 ? (
                <p className="shrink-0 text-sm text-slate-600">No PDFs attached yet.</p>
              ) : (
                <ul className="shrink-0 space-y-1">
                  {documents.map((document) => (
                    <DocumentListRow
                      key={document.id}
                      document={document}
                      selected={selectedDocument?.id === document.id}
                      canRemove={!pdfsLocked && !deleteMutation.isPending}
                      onSelect={() => setSelectedDocumentId(document.id)}
                      onRemove={() => deleteMutation.mutate(document.id)}
                    />
                  ))}
                </ul>
              )}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <div className="shrink-0 border-b border-slate-200 px-3 py-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Preview</h3>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-0">
                  {previewQuery.isLoading ? (
                    <p className="py-12 text-center text-sm text-slate-500">Loading preview…</p>
                  ) : previewQuery.isError ? (
                    <p className="px-3 py-8 text-center text-sm text-rose-700">Could not load this PDF preview.</p>
                  ) : previewQuery.data?.signedUrl != null && selectedDocument != null ? (
                    <div className="flex min-h-0 flex-1 flex-col">
                      <HubBlobUrlPreview
                        previewUrl={previewQuery.data.signedUrl}
                        filename={previewQuery.data.filename || selectedDocument.filename}
                        contentType={previewQuery.data.contentType ?? selectedDocument.content_type}
                        fillAvailableHeight
                      />
                    </div>
                  ) : (
                    <p className="py-12 text-center text-sm text-slate-500">Select a PDF to preview it.</p>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
          </HubWorkspacePanel>
        </HubWorkspaceBody>
      )}
    </HubWorkspacePage>
  );
}

function DocumentListRow({
  document,
  selected,
  canRemove,
  onSelect,
  onRemove,
}: {
  document: SuntronicInvoiceDocumentRow;
  selected: boolean;
  canRemove: boolean;
  onSelect: () => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <li className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-sm",
          selected ? "bg-brand-50 font-medium text-brand-800" : "text-slate-600 hover:bg-slate-50",
        )}
      >
        {document.filename}
      </button>
      {canRemove ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={`Remove ${document.filename}`}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      ) : null}
    </li>
  );
}
